import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import Avatar from 'react-avatar';
import TextareaAutosize from 'react-textarea-autosize';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import {
  AtSign,
  Building2,
  ChevronDown,
  FileText,
  Globe,
  ImagePlus,
  Loader2,
  Lock,
  Search,
  X
} from 'lucide-react';
import {
  createPost,
  editPost,
  getUserById,
  getUsersByChar
} from '../../services/api/implementation/impl';
import { uploadImage } from '../../../shared/services/api/implementation/impl';
import useFeedProjects from '../feed/useFeedProjects';
import { MentionMenu } from '../../pages/LiveFeedCard';

const MAX_FILE_SIZE = 20 * 1024 * 1024;
// Same limit the API enforces; the counter here just says so before sending.
const MAX_POST_LENGTH = 2000;
// What the file picker offers; dropped files are checked against the same list.
const ACCEPT_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif', 'webp',
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt'
];
const ACCEPT = ACCEPT_EXTENSIONS.map((ext) => `.${ext}`).join(',');

const extensionOf = (name = '') => name.split('.').pop().toLowerCase();
const isImage = (name = '') => /\.(jpe?g|png|gif|webp)$/i.test(name);
const handleFor = (user) => `@${user.name.replace(/\s+/g, '_')}`;

// Looks up users for a search string, one request per pause in typing.
const useUserSearch = () => {
  const [results, setResults] = useState([]);
  const timer = useRef(null);

  const search = (text) => {
    clearTimeout(timer.current);
    if (!text) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const data = await getUsersByChar(text);
        setResults(
          Array.isArray(data) ? data.map((user) => ({ id: user.id, name: user.name })) : []
        );
      } catch {
        setResults([]);
      }
    }, 250);
  };

  useEffect(() => () => clearTimeout(timer.current), []);
  return { results, search, clear: () => search('') };
};

const VisibilityOption = ({ active, icon: Icon, label, onClick }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-action px-3 py-1.5 text-sm font-medium transition-colors ${
      active ? 'bg-surface text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
    }`}>
    <Icon size={14} />
    {label}
  </button>
);

// An attachment in the form: a saved one ({ id, name, url }) or a picked file
// that is uploaded only when the post is sent ({ key, name, file, preview }).
// Uploading on send means a file removed before posting never reaches S3.
const savedAttachment = (attachment) => ({
  key: `saved-${attachment.id}`,
  id: attachment.id,
  name: attachment.name,
  url: attachment.url
});

// Create a post, or, with `post`, edit that post (its project can't change).
export const CreatePost = ({ post, onSuccess, onClose, onDirtyChange }) => {
  const editing = !!post;
  const userId = Cookies.get('userId');
  const projects = useFeedProjects();

  const initial = useMemo(
    () => ({
      text: post?.post || '',
      isPublic: !!post?.is_public,
      attachments: (post?.attachments || []).map(savedAttachment),
      tagged: (post?.tagPostMapping || [])
        .map((mapping) => mapping?.user)
        .filter((user) => user?.id)
        .map((user) => ({ id: user.id, name: user.name }))
    }),
    [post]
  );

  const [text, setText] = useState(initial.text);
  const [projectId, setProjectId] = useState(post?.project_id ? String(post.project_id) : '');
  const [isPublic, setIsPublic] = useState(initial.isPublic);
  const [attachments, setAttachments] = useState(initial.attachments);
  const [uploading, setUploading] = useState(false);
  const [tagged, setTagged] = useState(initial.tagged);
  const [tagSearchOpen, setTagSearchOpen] = useState(false);
  const [tagQuery, setTagQuery] = useState('');
  const [dragging, setDragging] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);

  const textRef = useRef(null);
  const fileRef = useRef(null);
  const mentions = useUserSearch();
  const people = useUserSearch();

  const { data: userData } = useQuery({
    queryKey: ['userLite', userId],
    queryFn: () => getUserById(userId),
    enabled: !!userId,
    staleTime: 5 * 60_000
  });

  useEffect(() => {
    mixpanel.track(editing ? 'Edit Post Viewed' : 'Create Post Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId, editing]);

  // Local previews of picked images are object URLs; free them when the form
  // goes away.
  const previews = useRef(new Set());
  useEffect(
    () => () => previews.current.forEach((url) => URL.revokeObjectURL(url)),
    []
  );

  // Lets the modal ask before discarding only when there's something to lose.
  const sameIds = (a, b) =>
    a.length === b.length && a.every((item, index) => item.key === b[index].key);
  const isDirty = editing
    ? text !== initial.text ||
      isPublic !== initial.isPublic ||
      !sameIds(attachments, initial.attachments) ||
      tagged.map((t) => t.id).join() !== initial.tagged.map((t) => t.id).join()
    : !!(text.trim() || attachments.length || tagged.length || projectId);
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // react-query v5 calls it isPending (isLoading is gone from mutations).
  const { mutate: submitPost, isPending: isPosting } = useMutation({
    mutationFn: (payload) => (editing ? editPost({ id: post.id, ...payload }) : createPost(payload)),
    onSuccess: (saved) => {
      toast.success(editing ? 'Post updated' : 'Post created successfully');
      onSuccess(saved);
    },
    onError: (err) => {
      console.error(`Error ${editing ? 'updating' : 'creating'} post:`, err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          (editing ? 'Error updating post' : 'Error creating post')
      );
    }
  });

  const addTag = (user) =>
    setTagged((prev) => (prev.some((t) => t.id === user.id) ? prev : [...prev, user]));

  const removeTag = (id) => setTagged((prev) => prev.filter((t) => t.id !== id));

  // @mentions: suggest for the @word just before the caret.
  const onTextChange = (e) => {
    const value = e.target.value.slice(0, MAX_POST_LENGTH);
    setText(value);
    const beforeCaret = value.slice(0, e.target.selectionStart);
    const match = beforeCaret.match(/@(\w*)$/);
    mentions.search(match?.[1] || '');
  };

  const pickMention = (user) => {
    const el = textRef.current;
    const caret = el?.selectionStart ?? text.length;
    const before = text.slice(0, caret).replace(/@\w*$/, `${handleFor(user)} `);
    const next = before + text.slice(caret);
    setText(next);
    addTag(user);
    mentions.clear();
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(before.length, before.length);
    });
  };

  const pickPerson = (user) => {
    addTag(user);
    setTagQuery('');
    people.clear();
  };

  const addFiles = (fileList) => {
    const taken = new Set(attachments.map((a) => a.name));
    const picked = [];
    Array.from(fileList || []).forEach((file) => {
      if (!ACCEPT_EXTENSIONS.includes(extensionOf(file.name))) {
        toast.error(`"${file.name}" isn't a supported file type.`);
      } else if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" is over 20MB.`);
      } else if (taken.has(file.name)) {
        toast.error(`"${file.name}" is already added.`);
      } else {
        taken.add(file.name);
        const preview = isImage(file.name) ? URL.createObjectURL(file) : null;
        if (preview) previews.current.add(preview);
        picked.push({ key: `new-${file.name}-${file.lastModified}`, name: file.name, file, preview });
      }
    });
    if (picked.length) setAttachments((prev) => [...prev, ...picked]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeAttachment = (key) =>
    setAttachments((prev) => {
      const removed = prev.find((a) => a.key === key);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
        previews.current.delete(removed.preview);
      }
      return prev.filter((a) => a.key !== key);
    });

  // Uploads the picked files and returns every attachment as the API wants it:
  // saved ones by id, new ones by name and URL.
  const resolveAttachments = async () => {
    const fresh = attachments.filter((a) => a.file);
    let uploaded = [];
    if (fresh.length) {
      const formData = new FormData();
      fresh.forEach((a) => formData.append('files', a.file));
      const result = await uploadImage(formData);
      const files = result?.data?.files || [];
      uploaded = fresh.map((a, index) => {
        const match = files.find((f) => f.originalName === a.name) || files[index];
        if (!match?.presignedUrl) throw new Error(`"${a.name}" didn't upload`);
        return { name: a.name, url: match.presignedUrl };
      });
    }
    return [
      ...attachments.filter((a) => a.id).map((a) => ({ id: a.id, name: a.name, url: a.url })),
      ...uploaded
    ];
  };

  const hasContent = text.trim() || attachments.length > 0;
  const busy = uploading || isPosting;
  const canPost = !!projectId && hasContent && !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    setTriedSubmit(true);
    if (!canPost) return;
    let files;
    setUploading(true);
    try {
      files = await resolveAttachments();
    } catch (err) {
      console.error('Error uploading files:', err);
      toast.error('Could not upload the files. Please try again.');
      return;
    } finally {
      setUploading(false);
    }
    const payload = {
      post: text.trim(),
      is_public: isPublic,
      attachments: files,
      tag: tagged.map((t) => t.id)
    };
    submitPost(editing ? payload : { ...payload, project_id: projectId });
  };

  const previewFor = (file) => file.preview || file.url;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      {/* Author, project and audience */}
      <div className="flex flex-wrap items-center gap-3">
        <Avatar name={userData?.name || ''} size="44" round color="#49A4AB" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-gray-900">{userData?.name || ''}</p>
          <p className="text-xs text-gray-500">
            {isPublic ? 'Buyers following the project will see this' : 'Only your team will see this'}
          </p>
        </div>
        <div className="inline-flex rounded-control bg-surface-sunken p-1">
          <VisibilityOption
            active={!isPublic}
            icon={Lock}
            label="Private"
            onClick={() => setIsPublic(false)}
          />
          <VisibilityOption
            active={isPublic}
            icon={Globe}
            label="Public"
            onClick={() => setIsPublic(true)}
          />
        </div>
      </div>

      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-gray-700">
          Project <span className="text-danger">*</span>
        </span>
        <span
          className={`relative flex h-11 items-center rounded-control border bg-surface transition-colors focus-within:border-brand-600 ${
            triedSubmit && !projectId ? 'border-danger' : 'border-hairline'
          } ${editing ? 'bg-surface-sunken' : ''}`}>
          <Building2 size={16} className="pointer-events-none absolute left-3 text-gray-500" />
          <select
            value={projectId}
            disabled={editing}
            title={editing ? "A post's project can't be changed" : undefined}
            onChange={(e) => setProjectId(e.target.value)}
            className="h-full w-full cursor-pointer appearance-none bg-transparent pl-9 pr-9 text-sm text-gray-900 outline-none disabled:cursor-not-allowed">
            <option value="">Select a project</option>
            {editing && !projects.some((p) => String(p.id) === projectId) && (
              <option value={projectId}>{post?.project_name || 'This project'}</option>
            )}
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <ChevronDown size={16} className="pointer-events-none absolute right-3 text-gray-500" />
        </span>
        {triedSubmit && !projectId && (
          <span className="mt-1 block text-xs text-danger">Choose the project this is about.</span>
        )}
      </label>

      {/* Post text */}
      <div>
        <div
          className={`relative rounded-control border bg-surface px-4 py-3 transition-colors focus-within:border-brand-600 ${
            triedSubmit && !hasContent ? 'border-danger' : 'border-hairline'
          }`}>
          <TextareaAutosize
            ref={textRef}
            autoFocus
            value={text}
            onChange={onTextChange}
            onBlur={() => setTimeout(mentions.clear, 150)}
            minRows={4}
            maxRows={12}
            placeholder="What's happening on site? Type @ to tag someone."
            className="w-full resize-none bg-transparent text-[15px] leading-relaxed text-gray-900 outline-none placeholder:text-gray-400"
          />
          {mentions.results.length > 0 && (
            <MentionMenu
              className="left-0 top-full mt-1"
              users={mentions.results}
              onPick={pickMention}
            />
          )}
          <p className="mt-1 text-right text-xs text-gray-400">
            {text.length}/{MAX_POST_LENGTH}
          </p>
        </div>
        {triedSubmit && !hasContent && (
          <span className="mt-1 block text-xs text-danger">Write something or add a file.</span>
        )}
      </div>

      {/* Tagged people */}
      <div>
        <div className="flex flex-wrap items-center gap-2">
          {tagged.map((user) => (
            <span
              key={user.id}
              className="inline-flex items-center gap-1.5 rounded-pill bg-brand-100 py-1 pl-1 pr-2 text-sm font-medium text-brand-800">
              <Avatar name={user.name} size="20" round />
              {user.name}
              <button
                type="button"
                aria-label={`Remove ${user.name}`}
                onClick={() => removeTag(user.id)}
                className="rounded-full p-0.5 text-brand-700 hover:bg-brand-50">
                <X size={13} />
              </button>
            </span>
          ))}
          {!tagSearchOpen && (
            <button
              type="button"
              onClick={() => setTagSearchOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-pill border border-dashed border-hairline-strong px-3 py-1 text-sm font-medium text-gray-600 hover:border-brand-600 hover:text-brand-700">
              <AtSign size={14} />
              Tag people
            </button>
          )}
        </div>
        {tagSearchOpen && (
          <div className="relative mt-2">
            <span className="relative flex h-10 items-center rounded-control border border-hairline bg-surface focus-within:border-brand-600">
              <Search size={15} className="pointer-events-none absolute left-3 text-gray-500" />
              <input
                autoFocus
                value={tagQuery}
                onChange={(e) => {
                  setTagQuery(e.target.value);
                  people.search(e.target.value.trim());
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.preventDefault();
                  if (e.key === 'Escape') setTagSearchOpen(false);
                }}
                placeholder="Search people to tag…"
                className="h-full w-full bg-transparent pl-9 pr-9 text-sm outline-none placeholder:text-gray-400"
              />
              <button
                type="button"
                aria-label="Close"
                onClick={() => {
                  setTagSearchOpen(false);
                  setTagQuery('');
                  people.clear();
                }}
                className="absolute right-2 rounded-action p-1 text-gray-500 hover:bg-surface-sunken">
                <X size={15} />
              </button>
            </span>
            {tagQuery && people.results.length > 0 && (
              <MentionMenu
                className="left-0 top-full mt-1"
                users={people.results.filter((u) => !tagged.some((t) => t.id === u.id))}
                onPick={pickPerson}
              />
            )}
          </div>
        )}
        {tagged.length > 0 && (
          <p className="mt-1.5 text-xs text-gray-500">
            Only people in the project&apos;s company can be tagged. Tagged people get a
            notification when you post.
          </p>
        )}
      </div>

      {/* Attachments */}
      <div>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
        {attachments.length > 0 && (
          <div className="mb-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
            {attachments.map((file) => (
              <div
                key={file.key}
                className="group relative aspect-square overflow-hidden rounded-control border border-hairline bg-surface-muted">
                {isImage(file.name) && previewFor(file) ? (
                  <img src={previewFor(file)} alt={file.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center gap-1 p-2 text-center">
                    <FileText size={24} className="text-gray-400" />
                    <span className="line-clamp-2 break-all text-xs text-gray-600">
                      {file.name}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  disabled={busy}
                  onClick={() => removeAttachment(file.key)}
                  className="absolute right-1 top-1 rounded-full bg-gray-900/70 p-1 text-white hover:bg-gray-900 disabled:opacity-50">
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            addFiles(e.dataTransfer.files);
          }}
          className={`flex w-full items-center justify-center gap-3 rounded-control border border-dashed px-4 py-4 text-sm transition-colors ${
            dragging
              ? 'border-brand-600 bg-brand-50 text-brand-700'
              : 'border-hairline-strong text-gray-600 hover:border-brand-600 hover:text-brand-700'
          }`}>
          <ImagePlus size={20} />
          <span>
            <span className="font-medium">Add photos or files</span>
            <span className="text-gray-400"> · images, PDF or Office files, up to 20MB each</span>
          </span>
        </button>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 border-t border-hairline pt-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-control border border-hairline-strong px-4 py-2 text-sm font-medium text-gray-700 hover:bg-surface-sunken">
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-control bg-brand-700 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60">
          {busy && <Loader2 size={15} className="animate-spin" />}
          {uploading ? 'Uploading…' : editing ? 'Save' : 'Post'}
        </button>
      </div>
    </form>
  );
};
